import React from 'react';
import { SlideLayout } from './SlideLayout';
import { motion } from 'framer-motion';
import { Flame, Target, Users, Zap, Award, CheckCircle2 } from 'lucide-react';
export function Slide5_Forge() {
  const day1Curriculum = ['The 12 Truths of Direct Sales', 'Core Values of Top Performers', 'Role Clarity & Expectations', 'Personal Fuel – Vision & Goals', 'Structure & Accountability', 'The Cycle of Achievement'];
  const day2Curriculum = ['Mastering the Approach', 'Talking to Strangers', 'Meta-Verbal Mastery', 'The 10-Step Approach Cycle', 'Handling Objections', 'Rules & Standards', '30-Day Commitment Challenge'];
  const characteristics = [{
    icon: Zap,
    title: 'Mindset + Skillset',
    desc: 'Rookies gain identity, purpose, and habits'
  }, {
    icon: Users,
    title: 'Universal & Transferable',
    desc: 'Works across all door-to-door industries'
  }, {
    icon: Target,
    title: 'Immersive & Realistic',
    desc: 'No sugarcoating—reps leave clear on the challenge'
  }, {
    icon: Award,
    title: 'Commitment-Driven',
    desc: 'Ends with a 30-day pledge for accountability'
  }];
  return <SlideLayout data-id="element-155">
      <div className="flex flex-col h-full justify-center space-y-8 sm:space-y-12" data-id="element-156">
        {/* Header */}
        <div className="text-center space-y-6" data-id="element-157">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500/20 to-red-600/20 border border-orange-500/30 mb-4" data-id="element-158">
            <Flame className="w-10 h-10 text-orange-500" data-id="element-159" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-bold text-white" data-id="element-160">
            Sales School
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-161">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 font-semibold tracking-wide uppercase text-sm" data-id="element-162">
              Two-Day Live Sales School
            </span>
          </motion.div>
        </div>

        {/* Curriculum Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto w-full" data-id="element-163">
          {/* Day 1 */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-slate-900/40 border border-slate-800 rounded-xl p-6 sm:p-8 flex flex-col" data-id="element-164">
            <div className="mb-6 pb-4 border-b border-slate-800" data-id="element-165">
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-166">Day 1</h3>
              <p className="text-orange-400 font-medium" data-id="element-167">
                Foundation, Mindset & Structure
              </p>
              <p className="text-slate-500 text-sm mt-1" data-id="element-168">7.5 Hours</p>
            </div>

            <ul className="space-y-3 mb-8 flex-1" data-id="element-169">
              {day1Curriculum.map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-170">
                  <CheckCircle2 className="w-5 h-5 text-orange-500/50 mr-3 shrink-0 mt-0.5" data-id="element-171" />
                  <span data-id="element-172">{item}</span>
                </li>)}
            </ul>

            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50" data-id="element-173">
              <p className="text-sm text-slate-400" data-id="element-174">
                <span className="text-orange-400 font-bold uppercase text-xs tracking-wider block mb-1" data-id="element-175">
                  Outcome
                </span>
                Clear-eyed, motivated, and personally committed
              </p>
            </div>
          </motion.div>

          {/* Day 2 */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-slate-900/40 border border-slate-800 rounded-xl p-6 sm:p-8 flex flex-col" data-id="element-176">
            <div className="mb-6 pb-4 border-b border-slate-800" data-id="element-177">
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-178">Day 2</h3>
              <p className="text-orange-400 font-medium" data-id="element-179">
                Skills, Communication & Confidence
              </p>
              <p className="text-slate-500 text-sm mt-1" data-id="element-180">7.5 Hours</p>
            </div>

            <ul className="space-y-3 mb-8 flex-1" data-id="element-181">
              {day2Curriculum.map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-182">
                  <CheckCircle2 className="w-5 h-5 text-orange-500/50 mr-3 shrink-0 mt-0.5" data-id="element-183" />
                  <span data-id="element-184">{item}</span>
                </li>)}
            </ul>

            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50" data-id="element-185">
              <p className="text-sm text-slate-400" data-id="element-186">
                <span className="text-orange-400 font-bold uppercase text-xs tracking-wider block mb-1" data-id="element-187">
                  Outcome
                </span>
                Knowing what to say and believing they can do it
              </p>
            </div>
          </motion.div>
        </div>

        {/* Characteristics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto w-full" data-id="element-188">
          {characteristics.map((char, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4 + idx * 0.1
        }} className="bg-slate-900/30 border border-slate-800/50 rounded-lg p-4 hover:bg-slate-800/50 transition-colors" data-id="element-189">
              <char.icon className="w-6 h-6 text-orange-500 mb-3" data-id="element-190" />
              <h4 className="text-white font-bold text-sm mb-1" data-id="element-191">
                {char.title}
              </h4>
              <p className="text-slate-400 text-xs leading-relaxed" data-id="element-192">
                {char.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </SlideLayout>;
}